import t from 'prop-types';
import { Link } from 'react-router-dom';
import { Menu as ArkMenu } from '@ark-ui/react/menu';
import { Portal } from '@ark-ui/react/portal';
import { calcPlacement, getChildSlots } from '../utils.js';
import { Icon } from './Icon.js';
import classNames from 'classnames';

/**
 * Renderer for a button menu item.
 */
const ButtonItem = ({ label, value, onClick, icon }) => (
  <ArkMenu.Item value={value || label} asChild tabIndex={-1}>
    <button
      type="button"
      onClick={onClick}
      className={classNames('dropdown-item')}
    >
      {icon && <Icon name={icon} md></Icon>}
      <span className="dropdown-item-label">{label}</span>
    </button>
  </ArkMenu.Item>
);

/**
 * Renderer for a link manu item, which can be relative (internal) or absolute
 * (external).
 */
const LinkItem = ({ label, value, to, href, icon, target, onClick }) => (
  <ArkMenu.Item value={value || label} asChild>
    {href ? (
      <a
        href={href}
        target={target}
        onClick={onClick}
        className={classNames('dropdown-item')}
        tabIndex={-1}
      >
        {icon && <Icon name={icon} md></Icon>}
        <span className="dropdown-item-label">{label}</span>
      </a>
    ) : (
      <Link
        to={to}
        onClick={onClick}
        className={classNames('dropdown-item')}
        tabIndex={-1}
      >
        {icon && <Icon name={icon} md></Icon>}
        <span className="dropdown-item-label">{label}</span>
      </Link>
    )}
  </ArkMenu.Item>
);

/**
 * Renderer for a divider menu item.
 */
const DividerItem = () => <ArkMenu.Separator className="my-2" />;

/**
 * Renderer for a group menu item, which renders a header with items below it.
 */
const GroupItem = ({ label, children = [] }) => {
  return (
    <ArkMenu.ItemGroup>
      <ArkMenu.ItemGroupLabel className="p-2 pb-1 text-uppercase fw-medium xsmall">
        {label}
      </ArkMenu.ItemGroupLabel>
      <Items items={children} />
    </ArkMenu.ItemGroup>
  );
};

const NestedItem = ({ label, children = [], onSelect }) => {
  return (
    <ArkMenu.Root onSelect={onSelect}>
      <ArkMenu.TriggerItem className="dropdown-item justify-content-between">
        <span className="dropdown-item-label">{label}</span>
        <Icon name="chevron-right" sm />
      </ArkMenu.TriggerItem>
      <ArkMenu.Positioner>
        <ArkMenu.Content className="dropdown-menu show m-1 position-static">
          <Items items={children} onSelect={onSelect} />
        </ArkMenu.Content>
      </ArkMenu.Positioner>
    </ArkMenu.Root>
  );
};

/**
 * Renders a list of items, selecting the correct renderer for each.
 */
const Items = ({ items = [], onSelect }) =>
  items.map((item, index) => {
    if (item.type === 'group') {
      return <GroupItem {...item} key={`group-${index}`} />;
    } else if (item.type === 'divider') {
      return <DividerItem key={`divider-${index}`} />;
    } else if (typeof item.to === 'string' || typeof item.href === 'string') {
      return <LinkItem {...item} key={`link-${index}`} />;
    } else if (Array.isArray(item.children)) {
      return (
        <NestedItem {...item} key={`nexted-${index}`} onSelect={onSelect} />
      );
    } else {
      return <ButtonItem {...item} key={`button-${index}`} />;
    }
  });

/**
 * @typedef {Object} MenuItem An object defining the properties of a menu item.
 *  The `type`, `onClick`, and `to` properties are mutually exclusive and are
 *  used to determine the type of menu item to render.
 * @property {string} [label] Text label of the menu item.
 * @property {string} [value] Text value for the item, used as a parameter in
 *  the onSelect function. Defaults to label if not provided.
 * @property {string} [icon] Icon name for the menu item.
 * @property {('group'|'divider')} [type] Type of special menu item.
 * @property {Function} [onClick] Click handler for button menu items. Used when
 *  `type` is not provided.
 * @property {string} [href] Path for external link menu items. Used when
 *  `type` and `onClick` are not provided.
 * @property {string} [to] Path for internal link menu items.
 *  Used when `href`, `type`, and `onClick` are not provided.
 * @property {string} [target] Target attribute for anchor tag for link menu
 *  items. Used when `href` is provided.
 */

/**
 * A dropdown menu of actionable items.
 *
 * @param {Object} props Props object
 * @param {MenuItem[]} props.items A list of items to render in the menu.
 * @param {boolean} [props.open] Is the menu open.
 *  When provided, the component will be set to controlled mode, and this flag
 *  will be used to determine if it's open or not. If omitted, the component
 *  will be set to uncontrolled mode.
 * @param {Function} [props.onOpenChange] If the component is controlled, this
 *  function is triggered when the open state should be changed, and it should
 *  modify the `open` prop. If the component is uncontrolled, this function is
 *  triggered as a callback whenever the open state changes.
 *  The function is passed one parameter, which is an object with an `open`
 *  property defining what the new state of the component is or should be.
 * @param {Function} [props.onSelect] Function triggered when an item is
 *  selected. It is passed an object with the item's `value` property as an
 *  argument.
 * @param {('bottom'|'top'|'left'|'right')} [props.position=bottom] The position
 *  of the menu relative to the trigger.
 * @param {('start'|'middle'|'end')} [props.alignment=start] The alignment of
 *  the menu relative to the trigger.
 * @param {(true|'alt')} [props.withBadges] Renders the options as badges in
 *  the menu. If set to 'alt', the badges will be rendered in an alternative
 *  color.
 * @param {JSX.Element|JSX.Element[]} [props.children] Elements to inject into
 *  available slots in the menu. Available slots are:
 *  - trigger: Component that toggles the menu open state when interacted with.
 */
export const Menu = ({
  items,
  open,
  onOpenChange,
  onSelect,
  position,
  alignment,
  withBadges,
  children,
}) => {
  const slots = getChildSlots(children, {
    componentName: 'Menu',
    requiredSlots: [],
    optionalSlots: ['trigger'],
  });
  const placement = calcPlacement(position, alignment);

  return (
    <ArkMenu.Root
      open={open}
      onOpenChange={onOpenChange}
      positioning={{ placement }}
      onSelect={onSelect}
    >
      {slots.trigger && (
        <ArkMenu.Trigger
          asChild
          // Don't propagate escape press from dropdown trigger to make sure it
          // doesn't close outer components like modals
          onKeyUp={e => e.key === 'Escape' && e.stopPropagation()}
          className="dropdown-trigger"
        >
          {slots.trigger}
        </ArkMenu.Trigger>
      )}
      <Portal>
        <ArkMenu.Positioner
          className={classNames(
            'dropdown',
            withBadges && {
              'dropdown--badged': withBadges,
              'dropdown--badged-alt': withBadges === 'alt',
            },
          )}
        >
          <ArkMenu.Content className="dropdown-menu show m-1 position-static">
            <Items items={items} onSelect={onSelect} />
          </ArkMenu.Content>
        </ArkMenu.Positioner>
      </Portal>
    </ArkMenu.Root>
  );
};

const itemType = {
  label: t.oneOfType([t.string, t.node]),
  value: t.string,
  icon: t.string,
  onClick: t.func,
  to: t.string,
  href: t.string,
  target: t.string,
  type: t.oneOf(['group', 'divider']),
};
itemType.children = t.arrayOf(t.shape(itemType));

ButtonItem.propTypes = {
  label: itemType.label,
  value: itemType.value,
  icon: itemType.icon,
  onClick: itemType.onClick,
};

LinkItem.propTypes = {
  label: itemType.label,
  value: itemType.value,
  icon: itemType.icon,
  to: itemType.to,
  href: itemType.href,
  target: itemType.target,
};

DividerItem.propTypes = {
  type: t.oneOf(['divider']),
};

GroupItem.propTypes = {
  type: t.oneOf(['group']),
  label: itemType.label,
  children: itemType.children,
};

NestedItem.propTypes = {
  label: itemType.label,
  children: itemType.children,
};

Menu.propTypes = {
  items: t.arrayOf(t.shape(itemType)),
  open: t.bool,
  onOpenChange: t.func,
  position: t.oneOf(['bottom', 'top', 'left', 'right']),
  alignment: t.oneOf(['start', 'middle', 'end']),
  children: t.node,
};
